import { Input } from "components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "components/ui/popover";
import { Check, ChevronDown, Plus } from "lucide-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { fetchQuery, graphql, useRelayEnvironment } from "react-relay";

import type { SlackChannelTypeaheadChannelInfoQuery } from "./__generated__/SlackChannelTypeaheadChannelInfoQuery.graphql";
import type { SlackChannelTypeaheadQuery as SlackChannelTypeaheadQueryType } from "./__generated__/SlackChannelTypeaheadQuery.graphql";

export const SlackChannelTypeaheadQueryDocument = graphql`
  query SlackChannelTypeaheadQuery {
    slackChannels(types: "public_channel") {
      id
      name
      isPrivate
      isMember
      isArchived
    }
  }
`;

const channelInfoQuery = graphql`
  query SlackChannelTypeaheadChannelInfoQuery($channelId: String!) {
    slackChannelInfo(slackChannelId: $channelId) {
      id
      name
      isMember
      isPrivate
    }
  }
`;

interface SlackChannel {
  id: string;
  name: string;
  isPrivate: boolean;
  isMember: boolean;
  isArchived: boolean;
}

interface SlackChannelTypeaheadProps {
  value: string;
  onChange: (channelId: string, channelName: string, isMember: boolean, isPrivate: boolean) => void;
  className?: string;
  excludeChannelIds?: string[];
  /** When true, verifies bot membership by fetching fresh data from Slack API for the selected channel */
  verifyMembership?: boolean;
}

interface ChannelResultsListProps {
  channels: SlackChannel[];
  selectedChannelId: string;
  onSelect: (channel: SlackChannel) => void;
  onAddCustomChannel: () => void;
}

const ChannelResultsList = React.memo<ChannelResultsListProps>(function ChannelResultsList({
  channels,
  selectedChannelId,
  onSelect,
  onAddCustomChannel
}) {
  return (
    <>
      {channels.length > 0 && (
        <div className="p-0.5">
          {channels.map((channel) => (
            <button
              key={channel.id}
              onClick={() => onSelect(channel)}
              className={`
                w-full flex items-center justify-between px-2 py-1.5 text-xs rounded
                hover:bg-accent transition-colors text-left
                ${selectedChannelId === channel.id ? "bg-accent" : ""}
              `}
            >
              <div className="flex items-center gap-1.5 flex-1 min-w-0">
                <span className="text-muted-foreground">#</span>
                <span className="truncate text-foreground">{channel.name}</span>
                <span className="text-xs text-muted-foreground">({channel.id})</span>
                {channel.isPrivate && (
                  <span className="text-xs text-muted-foreground ml-1">(private)</span>
                )}
              </div>
              {selectedChannelId === channel.id && (
                <Check className="h-3 w-3 text-primary shrink-0" />
              )}
            </button>
          ))}
        </div>
      )}

      {/* Add custom channel option */}
      <div className={`${channels.length > 0 ? "border-t border-border mt-1 pt-1" : ""} p-0.5`}>
        <button
          onClick={onAddCustomChannel}
          className="w-full flex items-center gap-1.5 px-2 py-1.5 text-xs rounded hover:bg-accent transition-colors text-left"
        >
          <Plus className="h-3 w-3 text-primary shrink-0" />
          <span className="text-primary">Add Channel by ID</span>
        </button>
      </div>
    </>
  );
});

export default function SlackChannelTypeahead({
  value,
  onChange,
  className = "",
  excludeChannelIds = [],
  verifyMembership = false,
}: SlackChannelTypeaheadProps) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [triggerWidth, setTriggerWidth] = useState(0);
  const [isTyping, setIsTyping] = useState(false);
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [customChannelId, setCustomChannelId] = useState("");
  const [validationError, setValidationError] = useState("");
  const [isCheckingMembership, setIsCheckingMembership] = useState(false);
  const [channels, setChannels] = useState<SlackChannel[]>([]);
  const [isLoadingChannels, setIsLoadingChannels] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const customInputRef = useRef<HTMLInputElement>(null);
  const environment = useRelayEnvironment();

  useEffect(() => {
    let isMounted = true;

    const loadChannels = async () => {
      setIsLoadingChannels(true);
      setLoadError(null);

      try {
        const response = await fetchQuery<SlackChannelTypeaheadQueryType>(
          environment,
          SlackChannelTypeaheadQueryDocument,
          {}
        ).toPromise();

        if (!isMounted) {
          return;
        }

        const normalizedChannels = response?.slackChannels?.map((channel) => ({
          id: channel.id,
          name: channel.name,
          isPrivate: channel.isPrivate,
          isMember: channel.isMember,
          isArchived: channel.isArchived,
        })) ?? [];

        setChannels(normalizedChannels);
      } catch (error) {
        console.error("Failed to load Slack channels", error);
        if (!isMounted) {
          return;
        }

        setChannels([]);
        setLoadError("Unable to load Slack channels");
      } finally {
        if (isMounted) {
          setIsLoadingChannels(false);
        }
      }
    };

    loadChannels();

    return () => {
      isMounted = false;
    };
  }, [environment]);

  // Calculate Levenshtein distance between two strings
  const levenshteinDistance = (str1: string, str2: string): number => {
    const len1 = str1.length;
    const len2 = str2.length;
    const matrix: number[][] = [];

    for (let i = 0; i <= len1; i++) {
      matrix[i] = [i];
    }

    for (let j = 0; j <= len2; j++) {
      matrix[0][j] = j;
    }

    for (let i = 1; i <= len1; i++) {
      for (let j = 1; j <= len2; j++) {
        if (str1[i - 1] === str2[j - 1]) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1,
            matrix[i][j - 1] + 1,
            matrix[i - 1][j] + 1
          );
        }
      }
    }

    return matrix[len1][len2];
  };

  const filteredChannels = useMemo(() => {
    // Filter out archived and excluded channels
    const availableChannels = channels.filter(
      (ch) => !ch.isArchived && !excludeChannelIds.includes(ch.id)
    );

    if (!searchQuery) {
      // If no search query, show selected channel first if it exists
      if (value) {
        const selected = availableChannels.find((ch) => ch.id === value);
        if (selected) {
          const others = availableChannels.filter((ch) => ch.id !== value);
          // Sort others: members first, then non-members
          const sortedOthers = others.sort((a, b) => {
            if (a.isMember === b.isMember) return a.name.localeCompare(b.name);
            return a.isMember ? -1 : 1;
          });
          return [selected, ...sortedOthers];
        }
      }
      // Sort: members first, then non-members
      return availableChannels.sort((a, b) => {
        if (a.isMember === b.isMember) return a.name.localeCompare(b.name);
        return a.isMember ? -1 : 1;
      });
    }

    const query = searchQuery.toLowerCase();

    // Filter channels that contain the query
    const matchingChannels = availableChannels.filter((ch) =>
      ch.name.toLowerCase().includes(query)
    );

    // Sort by membership first, then edit distance
    return matchingChannels.sort((a, b) => {
      // Prioritize members first
      if (a.isMember !== b.isMember) {
        return a.isMember ? -1 : 1;
      }

      // Among same membership status, sort by edit distance
      const distanceA = levenshteinDistance(query, a.name.toLowerCase());
      const distanceB = levenshteinDistance(query, b.name.toLowerCase());

      // If edit distances are equal, sort alphabetically
      if (distanceA === distanceB) {
        return a.name.localeCompare(b.name);
      }

      return distanceA - distanceB;
    });
  }, [channels, searchQuery, value, excludeChannelIds]);

  // We only render the top five channels but keep the add-by-ID option available.
  const limitedChannels = useMemo(() => filteredChannels.slice(0, 5), [filteredChannels]);

  const selectedChannel = channels.find((ch) => ch.id === value);

  useEffect(() => {
    if (triggerRef.current) {
      setTriggerWidth(triggerRef.current.offsetWidth);
    }
  }, []);

  const handleSelect = async (channel: SlackChannel) => {
    setOpen(false);
    setSearchQuery("");
    setIsTyping(false);
    setShowCustomInput(false);
    setCustomChannelId("");

    // If verifyMembership is enabled, fetch fresh membership data from Slack API
    if (verifyMembership) {
      try {
        const response = await fetchQuery<SlackChannelTypeaheadChannelInfoQuery>(
          environment,
          channelInfoQuery,
          { channelId: channel.id }
        ).toPromise();

        const channelInfo = response?.slackChannelInfo;
        const isMember = channelInfo?.isMember ?? channel.isMember;
        const isPrivate = channelInfo?.isPrivate ?? channel.isPrivate;

        onChange(channel.id, channel.name, isMember, isPrivate);
      } catch (error) {
        console.error("Error verifying channel membership:", error);
        // Fall back to cached data on error
        onChange(channel.id, channel.name, channel.isMember, channel.isPrivate);
      }
    } else {
      onChange(channel.id, channel.name, channel.isMember, channel.isPrivate);
    }
  };

  const handleButtonClick = () => {
    if (!isTyping && !showCustomInput) {
      setIsTyping(true);
      setOpen(true);
      // Focus the input after a brief delay to ensure it's rendered
      setTimeout(() => {
        inputRef.current?.focus();
      }, 50);
    }
  };

  const handleAddCustomChannel = () => {
    setIsTyping(false);
    setSearchQuery("");
    setValidationError("");
    setShowCustomInput(true);
    // Use requestAnimationFrame for better timing
    requestAnimationFrame(() => {
      customInputRef.current?.focus();
    });
  };

  const validateChannelId = (channelId: string): boolean => {
    // Slack channel IDs start with C (public and private) or G (legacy private, deprecated 2021)
    // Format: C or G followed by uppercase alphanumeric characters (8+ chars)
    const channelIdRegex = /^(C|G)[A-Z0-9]{8,}$/;
    return channelIdRegex.test(channelId);
  };

  const handleCustomChannelSubmit = async () => {
    const trimmedId = customChannelId.trim().toUpperCase();
    if (!trimmedId) {
      setValidationError("Channel ID is required");
      return;
    }

    if (!validateChannelId(trimmedId)) {
      setValidationError("Invalid channel ID format");
      return;
    }

    // Fetch channel info to get name and membership status
    setIsCheckingMembership(true);
    try {
      const response = await fetchQuery<SlackChannelTypeaheadChannelInfoQuery>(
        environment,
        channelInfoQuery,
        { channelId: trimmedId }
      ).toPromise();

      const channelInfo = response?.slackChannelInfo;
      const channelName = channelInfo?.name || trimmedId;
      const isMember = channelInfo?.isMember ?? false;
      // If we can't get channel info, assume it's private (bot can't access private channels it's not in)
      const isPrivate = channelInfo ? (channelInfo.isPrivate ?? false) : true;

      // Add the channel to local state so it displays properly
      const newChannel: SlackChannel = {
        id: trimmedId,
        name: channelName,
        isPrivate: isPrivate,
        isMember: isMember,
        isArchived: false,
      };

      // Add to channels state if not already present
      setChannels((prevChannels) => {
        const exists = prevChannels.some((ch) => ch.id === trimmedId);
        if (exists) {
          return prevChannels;
        }
        return [...prevChannels, newChannel];
      });

      // Use the actual channel name if available, otherwise use channel ID
      onChange(trimmedId, channelName, isMember, isPrivate);
      setOpen(false);
      setShowCustomInput(false);
      setCustomChannelId("");
      setValidationError("");
      setIsTyping(false);
      setSearchQuery("");
    } catch (error) {
      console.error("Error fetching channel info:", error);
      // On error, add channel with ID as name
      const fallbackChannel: SlackChannel = {
        id: trimmedId,
        name: trimmedId,
        isPrivate: false,
        isMember: false,
        isArchived: false,
      };

      setChannels((prevChannels) => {
        const exists = prevChannels.some((ch) => ch.id === trimmedId);
        if (exists) {
          return prevChannels;
        }
        return [...prevChannels, fallbackChannel];
      });

      onChange(trimmedId, trimmedId, false, false);
      setOpen(false);
      setShowCustomInput(false);
      setCustomChannelId("");
      setValidationError("");
      setIsTyping(false);
      setSearchQuery("");
    } finally {
      setIsCheckingMembership(false);
    }
  };

  const handleCustomChannelKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleCustomChannelSubmit();
    } else if (e.key === "Escape") {
      setShowCustomInput(false);
      setCustomChannelId("");
      setValidationError("");
    }
  };

  // Handle popover close - always reset typing state and clear search
  useEffect(() => {
    if (!open) {
      setIsTyping(false);
      setSearchQuery("");
      setShowCustomInput(false);
      setCustomChannelId("");
      setValidationError("");
    }
  }, [open]);

  // Focus custom input when showCustomInput changes to true
  useEffect(() => {
    if (showCustomInput && customInputRef.current) {
      // Force focus after a slight delay to ensure DOM is ready
      const timeoutId = setTimeout(() => {
        customInputRef.current?.focus();
      }, 100);
      return () => clearTimeout(timeoutId);
    }
    return undefined;
  }, [showCustomInput]);

  return (
    <Popover open={open} onOpenChange={setOpen} modal={false}>
      <PopoverTrigger asChild>
        <div
          ref={triggerRef as any}
          onClick={handleButtonClick}
          className={`flex h-9 w-full items-center justify-between rounded-md border border-border bg-transparent px-3 py-2 text-sm cursor-pointer hover:bg-accent transition-colors ${className}`}
        >
          {isTyping && !showCustomInput ? (
            <>
              <Input
                ref={inputRef}
                placeholder="Search channels..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="bg-transparent border-0 h-auto p-0 focus-visible:ring-0 focus-visible:ring-offset-0"
            />
              <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </>
          ) : (
            <>
              <span className="truncate">
                {isLoadingChannels ? (
                  <span className="text-muted-foreground">Loading channels...</span>
                ) : loadError ? (
                  <span className="text-red-400">Unable to load channels</span>
                ) : !showCustomInput && selectedChannel ? (
                  <span className="flex items-center gap-1.5">
                    <span className="text-muted-foreground">#</span>
                    <span className="text-foreground">{selectedChannel.name}</span>
                    <span className="text-xs text-muted-foreground">({selectedChannel.id})</span>
                    {selectedChannel.isPrivate && (
                      <span className="text-xs text-muted-foreground ml-1">(private)</span>
                    )}
                  </span>
                ) : !showCustomInput && value ? (
                  <span className="flex items-center gap-1.5">
                    <span className="text-muted-foreground">#</span>
                    <span className="text-foreground">{value}</span>
                    <span className="text-xs text-muted-foreground">(custom)</span>
                  </span>
                ) : (
                  <span className="text-muted-foreground">Select a channel...</span>
                )}
              </span>
              <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </>
          )}
        </div>
      </PopoverTrigger>
      <PopoverContent
        className="p-0 bg-popover border-border shadow-md"
        side="bottom"
        align="start"
        sideOffset={0}
        style={{
          width: triggerWidth > 0 ? `${triggerWidth}px` : "100%",
        }}
        onOpenAutoFocus={(e) => {
          if (!showCustomInput) {
            e.preventDefault();
          }
        }}
      >
        {showCustomInput ? (
          <div
            className="p-3 space-y-2"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-xs text-muted-foreground mb-2">
              Enter the Slack channel ID (e.g., C08KSSHMHPT)
            </div>
            <input
              ref={customInputRef}
              value={customChannelId}
              onChange={(e) => {
                setCustomChannelId(e.target.value);
                setValidationError("");
              }}
              onKeyDown={handleCustomChannelKeyDown}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
              placeholder="C08KSSHMHPT"
              className={`flex h-8 w-full rounded-md border px-3 py-2 text-xs text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-0 ${
                validationError
                  ? "border-red-500 bg-red-900/20 focus-visible:ring-red-500"
                  : "border-border bg-card focus-visible:ring-primary"
              }`}
              autoFocus
            />
            {validationError && (
              <div className="text-xs text-red-400 -mt-1">
                {validationError}
              </div>
            )}
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => {
                  setShowCustomInput(false);
                  setCustomChannelId("");
                  setValidationError("");
                  setIsTyping(true);
                  setTimeout(() => {
                    inputRef.current?.focus();
                  }, 50);
                }}
                className="px-2 py-1 text-xs font-medium bg-muted/60 text-foreground rounded hover:bg-muted hover:text-foreground transition-colors border border-border"
              >
                Cancel
              </button>
              <button
                onClick={handleCustomChannelSubmit}
                disabled={!customChannelId.trim() || isCheckingMembership}
                className="px-2 py-1 text-xs font-medium bg-blue-600/80 text-white rounded hover:bg-blue-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                {isCheckingMembership ? "Checking..." : "Save"}
              </button>
            </div>
          </div>
        ) : isLoadingChannels ? (
          <div className="p-3 text-xs text-muted-foreground">Loading channels...</div>
        ) : (
          <div
            className="max-h-[300px] overflow-y-scroll scrollbar-thin scrollbar-thumb-muted-foreground/50 scrollbar-track-card"
            onWheel={(e) => {
              // Handle scroll manually
              const container = e.currentTarget;
              const delta = e.deltaY;
              container.scrollTop += delta;
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            {loadError && (
              <div className="px-3 py-2 text-xs text-red-400">
                {loadError}. You can still add a channel by ID below.
              </div>
            )}
            <ChannelResultsList
              channels={limitedChannels}
              selectedChannelId={value}
              onSelect={handleSelect}
              onAddCustomChannel={handleAddCustomChannel}
            />
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
